import React, { useEffect, useMemo, useState } from 'react';
import { Container, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiCalendar,
  FiEdit2,
  FiFilter,
  FiMapPin,
  FiSearch,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { MdOutlineBeachAccess } from 'react-icons/md';
import PackageSummaryBar from '../../components/common/PackageSummaryBar';
import PackageState from '../../components/common/PackageState';
import PackageCard from '../../components/PackageCard/PackageCard';
import PackageFilters from '../../components/PackageFilters/PackageFilters';
import PackageSearchForm from '../../components/PackageSearchForm/PackageSearchForm';
import { searchPackagesApi } from '../../api/packagesApi';
import {
  PACKAGE_ROUTES,
  PACKAGE_SORT_TABS,
} from '../../constants/packageConstants';
import {
  EMPTY_PACKAGE_FILTERS,
  applyPackageFilters,
  buildPackageSearchQuery,
  buildPackageSearchRequest,
  countActiveFilters,
  formatPrice,
  isPackageSearchValid,
  packageFilterOptions,
  packageFromPrice,
  parsePackageSearchQuery,
  paxSummary,
  prettyDestination,
  sortPackages,
} from '../../utils/packageUtils';
import '../../styles/packagesShared.scss';
import './PackageResultsPage.scss';

function ResultsSkeleton() {
  return (
    <div className="pkg-results__list" aria-busy="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="pkg-card pkg-card--skeleton">
          <span className="pkg-skeleton pkg-card__skeleton-media" />
          <div className="pkg-card__skeleton-body">
            <span className="pkg-skeleton pkg-skeleton--short" />
            <span className="pkg-skeleton pkg-skeleton--title" />
            <span className="pkg-skeleton" />
            <span className="pkg-skeleton" />
          </div>
        </div>
      ))}
    </div>
  );
}

const durationText = (s) => {
  if (!s.minDays && !s.maxDays) return 'Any length';
  return `${s.minDays || 1} - ${s.maxDays || s.minDays} days`;
};

function PackageResultsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const search = useMemo(
    () => parsePackageSearchQuery(location.search),
    [location.search],
  );
  const valid = isPackageSearchValid(search);

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null); // { read, searchId, packages }
  const [filters, setFilters] = useState(EMPTY_PACKAGE_FILTERS);
  const [sort, setSort] = useState('price');
  const [showFilters, setShowFilters] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!valid) return undefined;
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setFilters(EMPTY_PACKAGE_FILTERS);
      const read = await searchPackagesApi(
        buildPackageSearchRequest(search),
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setResult({
        read,
        searchId: read.data?.searchId || '',
        packages: Array.isArray(read.data?.packages)
          ? read.data.packages.filter((p) => p?.packageKey)
          : [],
      });
      setLoading(false);
    })();
    return () => controller.abort();
  }, [search, valid, attempt]);

  const packages = useMemo(() => result?.packages || [], [result]);
  const filterOptions = useMemo(
    () => packageFilterOptions(packages),
    [packages],
  );
  const visible = useMemo(
    () => sortPackages(applyPackageFilters(packages, filters), sort),
    [packages, filters, sort],
  );
  const cheapestKey = useMemo(() => {
    const priced = packages.filter((p) => packageFromPrice(p) != null);
    if (priced.length < 2) return '';
    return priced.reduce((a, b) =>
      packageFromPrice(b) < packageFromPrice(a) ? b : a,
    ).packageKey;
  }, [packages]);
  const activeCount = countActiveFilters(filters);
  const hasFilterGroups = ['duration', 'month', 'supplier'].some(
    (k) => filterOptions[k].length > 1,
  );

  const goLanding = () => navigate(PACKAGE_ROUTES.LANDING);

  const openPackage = (pkg) => {
    const params = new URLSearchParams(buildPackageSearchQuery(search));
    params.set('key', pkg.packageKey);
    params.set('sid', result?.searchId || '');
    navigate(`${PACKAGE_ROUTES.DETAILS}?${params.toString()}`);
  };

  const modify = (next) => {
    setModifyOpen(false);
    navigate(`${PACKAGE_ROUTES.RESULTS}?${buildPackageSearchQuery(next)}`);
  };

  if (!valid) {
    return (
      <div className="pkg-results">
        <Container className="pkg-results__main">
          <PackageState
            title="This search is incomplete"
            message="Pick a destination and travellers to see holiday packages."
            primaryText="Search packages"
            onPrimary={goLanding}
          />
        </Container>
      </div>
    );
  }

  const read = result?.read;
  const failed = !loading && read && !read.ok;
  const hasPackages = !loading && read?.ok && packages.length > 0;
  const showSidebar = hasPackages && hasFilterGroups;

  return (
    <div className="pkg-results">
      <PackageSummaryBar
        onBack={goLanding}
        backLabel="Back to search"
        items={[
          {
            icon: <FiMapPin />,
            label: 'Destination',
            value: prettyDestination(search.destination),
          },
          {
            icon: <FiCalendar />,
            label: 'Trip length',
            value: durationText(search),
          },
          {
            icon: <FiUsers />,
            label: 'Travellers',
            value: paxSummary(search.pax),
          },
          search.maxPrice && {
            icon: <FiSearch />,
            label: 'Budget',
            value: `Under ${formatPrice(search.maxPrice)}`,
          },
        ]}
        actions={
          <button
            type="button"
            className="pkg-summary-bar__btn"
            onClick={() => setModifyOpen(true)}
          >
            <FiEdit2 /> Modify Search
          </button>
        }
      />

      <Container className="pkg-results__main">
        <div
          className={`pkg-results__layout ${
            showSidebar ? '' : 'pkg-results__layout--single'
          }`}
        >
          {showSidebar && (
            <div
              className={`pkg-results__sidebar ${
                showFilters ? 'pkg-results__sidebar--open' : ''
              }`}
            >
              <PackageFilters
                options={filterOptions}
                filters={filters}
                onChange={setFilters}
                activeCount={activeCount}
                onClose={() => setShowFilters(false)}
              />
            </div>
          )}

          <main className="pkg-results__content">
            {hasPackages && (
              <div className="pkg-results__toolbar">
                <div className="pkg-results__count">
                  {visible.length === packages.length
                    ? `${packages.length} package${
                        packages.length === 1 ? '' : 's'
                      } for ${prettyDestination(search.destination)}`
                    : `Showing ${visible.length} of ${packages.length} packages`}
                </div>
                <div
                  className="pkg-results__sort"
                  role="group"
                  aria-label="Sort packages"
                >
                  {PACKAGE_SORT_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      aria-pressed={sort === tab.key}
                      className={`pkg-results__sort-tab ${
                        sort === tab.key ? 'pkg-results__sort-tab--active' : ''
                      }`}
                      onClick={() => setSort(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {showSidebar && (
                  <button
                    type="button"
                    className="pkg-results__filter-toggle"
                    aria-expanded={showFilters}
                    onClick={() => setShowFilters((v) => !v)}
                  >
                    <FiFilter /> Filters
                    {activeCount > 0 && <em>{activeCount}</em>}
                  </button>
                )}
              </div>
            )}

            {loading && <ResultsSkeleton />}

            {failed && (
              <PackageState
                title={
                  read.network
                    ? 'We could not reach the server'
                    : 'Search did not go through'
                }
                message={
                  read.network
                    ? 'Check your connection and try again.'
                    : read.message || 'Please try again in a moment.'
                }
                reference={read.correlationId}
                primaryText="Try again"
                onPrimary={() => setAttempt((n) => n + 1)}
                secondaryText="New search"
                onSecondary={goLanding}
              />
            )}

            {!loading && read?.ok && packages.length === 0 && (
              <PackageState
                icon={<MdOutlineBeachAccess />}
                title="No packages for this search"
                message="Try a different destination, a wider trip length or a higher budget."
                primaryText="Modify search"
                onPrimary={() => setModifyOpen(true)}
              />
            )}

            {hasPackages && visible.length === 0 && (
              <PackageState
                icon={<FiFilter />}
                title="No packages match these filters"
                primaryText="Clear filters"
                onPrimary={() => setFilters(EMPTY_PACKAGE_FILTERS)}
              />
            )}

            {hasPackages && visible.length > 0 && (
              <div className="pkg-results__list">
                {visible.map((pkg) => (
                  <PackageCard
                    key={pkg.packageKey}
                    pkg={pkg}
                    cheapest={pkg.packageKey === cheapestKey}
                    onOpen={openPackage}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </Container>

      <Modal
        show={modifyOpen}
        onHide={() => setModifyOpen(false)}
        size="lg"
        centered
        dialogClassName="pkg-modal"
      >
        <Modal.Body>
          <button
            type="button"
            className="pkg-modal__close"
            onClick={() => setModifyOpen(false)}
            aria-label="Close"
          >
            <FiX />
          </button>
          <h3 className="pkg-modal__title">Modify your search</h3>
          <p className="pkg-modal__sub">
            Change the destination, trip length, travellers or budget.
          </p>
          <PackageSearchForm
            variant="modal"
            initialValues={search}
            onSearch={modify}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default PackageResultsPage;
